import React, { createContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import * as Sentry from "@sentry/react";
import { Widget } from "../types/platformSetting.ts";

interface ReservationContextType {
    widget: Widget | null;
}

const defaultReservationContext: ReservationContextType = {
    widget: null,
};

export const ReservationContext = createContext<ReservationContextType>(defaultReservationContext);

interface ReservationProviderProps {
    children: ReactNode;
}

const ReservationProvider: React.FC<ReservationProviderProps> = ({ children }) => {
    const [widget, setWidget] = useState<Widget | null>(null);
    const fetchedRef = useRef(false);

    const fetchWidgetCode = useCallback(async () => {
        if (fetchedRef.current) return;
        fetchedRef.current = true;

        const searchParams = new URLSearchParams(window.location.search);
        const platformSettingId = searchParams.get('platform_setting_id');
        const lineUid = searchParams.get('line_uid');

        if (!platformSettingId || !lineUid) {
            const error = new Error("Missing required reservation parameters");
            const eventId = Sentry.captureException(error);
            window.location.replace(`/error?eventId=${eventId}&code=400`);
            return;
        }

        try {
            const url = `${import.meta.env.VITE_API_HOST}/api/v2/platform-settings/${platformSettingId}/widget?external_type=line&external_member_id=${lineUid}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorMessage = `API request failed with status ${response.status}`;
                const requestId = response.headers.get('X-Request-Id') || '';
                const error = new Error(errorMessage);
                const eventId = Sentry.captureException(error);
                let redirectUrl = `/error?eventId=${eventId}`;
                if (requestId) {
                    redirectUrl += `&requestId=${encodeURIComponent(requestId)}`;
                }
                window.location.replace(redirectUrl);
                return;
            }

            const data: Widget = await response.json();
            if (!data.widget_code) {
                const error = new Error('Widget code not found in response');
                const eventId = Sentry.captureException(error);
                window.location.replace(`/error?eventId=${eventId}&code=404`);
                return;
            }

            setWidget(data);
            if (data.name) {
                document.title = data.name;
            }
        } catch (error) {
            const eventId = Sentry.captureException(error);
            window.location.replace(`/error?eventId=${eventId}&code=500`);
        }
    }, []);

    useEffect(() => {
        if (!widget) {
            fetchWidgetCode();
        }
    }, [fetchWidgetCode, widget]);

    return (
        <ReservationContext.Provider value={{ widget }}>
            {children}
        </ReservationContext.Provider>
    );
};

export default ReservationProvider; 